import React, { useRef, useState, useEffect } from "react";
import { useTheme, Tooltip, ClickAwayListener } from "@mui/material";
import { InfoOutlined, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import styled from "@xstyled/styled-components";
import { tokens } from "../../utils/theme";

import { copyTableAsJSON } from "../../utils/clipboardUtils";
import { trackEvent } from "../../utils/tracker";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
    Panel,
    PanelHeader,
    PanelTitle,
    TableWrapper,
    Table,
    Thead,
    Th,
    Tr,
    Td,
    TdSmall,
    ScrollRail,
    ScrollThumb,
    ScrollRailVertical,
    ScrollThumbVertical,
    Btn,
    BtnIcon,
    SectionHeaderRow,
    SectionHeaderCell
} from "../../components/SharedPageStyles";
import CustomSelect from "../../components/CustomSelect";

const ImpactBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
        if (props.level?.includes("High")) return "rgba(244, 67, 54, 0.15)";
        if (props.level?.includes("Medium")) return "rgba(255, 152, 0, 0.15)";
        return "rgba(76, 175, 80, 0.15)";
    }};
  color: ${props => {
        if (props.level?.includes("High")) return tokens(props.theme.palette.mode).redAccent[500];
        if (props.level?.includes("Medium")) return tokens(props.theme.palette.mode).orangeAccent[500];
        return tokens(props.theme.palette.mode).greenAccent[500];
    }};
  border: 1px solid ${props => {
        if (props.level?.includes("High")) return "rgba(244, 67, 54, 0.3)";
        if (props.level?.includes("Medium")) return "rgba(255, 152, 0, 0.3)";
        return "rgba(76, 175, 80, 0.3)";
    }};
`;

const CatalystText = styled.span`
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
`;

const formatNextRelease = (value) => {
    if (!value || value === "N/A") return value || "-";
    // Attempt to parse date
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            // If not a valid date object, try simple string manipulation if it looks like HH:MM:SS
            let str = String(value);
            if (str.match(/:\d{2}:\d{2}/)) {
                return str.substring(0, str.lastIndexOf(':'));
            }
            return str;
        }

        // Format: "Jan 15, 14:30" (Paris Time)
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Europe/Paris'
        });
    } catch (e) {
        return value;
    }
};



const EditableInput = styled.input`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
`;

const EditableTextArea = styled.textarea`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  padding-right: 36px; /* Space for arrows */
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  min-height: 60px;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollControls = styled.div`
  position: absolute;
  right: 4px;
  top: 4px;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
  pointer-events: none;
`;

const ScrollButton = styled.div`
  pointer-events: auto;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 2px;
  transition: all 0.2s;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)'};

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.95)'};
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const ModalContent = styled.div`
  background: #1e2130;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

const CheckboxContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;
  position: relative;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[500]
        : 'transparent'};
  border: 2px solid ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[500]
        : tokens(props.theme.palette.mode).grey[500]};
  border-radius: 4px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}40; 
  }

  &:hover {
     border-color: ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[600]
        : tokens(props.theme.palette.mode).blueAccent[400]};
  }
`;

const Icon = styled.svg`
  fill: none;
  stroke: ${props => props.theme.palette.mode === 'dark' ? '#000' : '#fff'};
  stroke-width: 3px;
  width: 12px;
  height: 12px;
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
`;

const CustomCheckbox = ({ checked, indeterminate, onChange, ...props }) => (
    <CheckboxContainer onClick={(e) => {
        e.stopPropagation();
        onChange({ target: { checked: !checked } });
    }}>
        <HiddenCheckbox checked={checked} onChange={onChange} {...props} />
        <StyledCheckbox checked={checked} indeterminate={indeterminate}>
            {indeterminate ? (
                <Icon viewBox="0 0 24 24" visible={true}>
                    <line x1="4" y1="12" x2="20" y2="12" />
                </Icon>
            ) : (
                <Icon viewBox="0 0 24 24" visible={checked}>
                    <polyline points="20 6 9 17 4 12" />
                </Icon>
            )}
        </StyledCheckbox>
    </CheckboxContainer>
);

const EditableCell = ({ value, onChange, type = "text" }) => {
    const [localValue, setLocalValue] = useState(value);
    const [showScroll, setShowScroll] = useState(false);
    const textareaRef = useRef(null);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        if (textareaRef.current) {
            const { scrollHeight, clientHeight } = textareaRef.current;
            setShowScroll(scrollHeight > clientHeight);
        }
    }, [localValue]);

    const handleBlur = () => {
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const handleScroll = (direction) => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop += direction * 20;
        }
    };

    if (type === "textarea") {
        return (
            <TextAreaWrapper>
                <EditableTextArea
                    ref={textareaRef}
                    value={localValue}
                    onChange={(e) => setLocalValue(e.target.value)}
                    onBlur={handleBlur}
                    onClick={(e) => e.stopPropagation()}
                />
                {showScroll && (
                    <ScrollControls>
                        <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(-1); }} title="Scroll Up">
                            <svg viewBox="0 0 24 24">
                                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                            </svg>
                        </ScrollButton>
                        <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(1); }} title="Scroll Down">
                            <svg viewBox="0 0 24 24">
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                            </svg>
                        </ScrollButton>
                    </ScrollControls>
                )}
            </TextAreaWrapper>
        );
    }
    return (
        <EditableInput
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
        />
    );
};

const InfoIconWithTooltip = ({ title }) => {
    const [open, setOpen] = useState(false);

    const handleTooltipClose = () => {
        setOpen(false);
    };

    const handleTooltipOpen = () => {
        setOpen(true);
    };

    return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip
                    PopperProps={{
                        disablePortal: false,
                        style: { zIndex: 1500 }
                    }}
                    onClose={handleTooltipClose}
                    open={open}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={
                        <div style={{ fontSize: '12px', lineHeight: '1.4', maxWidth: '250px', whiteSpace: 'normal', textTransform: 'none' }}>
                            {title}
                        </div>
                    }
                    arrow
                    placement="right"
                >
                    <InfoOutlined
                        onClick={handleTooltipOpen}
                        style={{ fontSize: '16px', opacity: 0.7, cursor: 'pointer', transform: 'translateY(-1.5px)' }}
                    />
                </Tooltip>
            </div>
        </ClickAwayListener>
    );
};

const EconomicTable = ({ data, title, defaultRowData = {}, groupBy, groupOrder, groupDescriptions, useDataHook, showNextRelease = true }) => {
    const theme = useTheme();
    const { rows, deleteRow, updateRow, getOriginalIndex, setAllData, addRow, reorderRows, impactFilter, setImpactFilter } = useDataHook();
    const [isEditMode, setIsEditMode] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});

    const [dateSortDirection, setDateSortDirection] = useState('desc'); // 'asc' | 'desc'
    const [impactSortDirection, setImpactSortDirection] = useState(null); // null | 'asc' | 'desc'

    // Group data if groupBy is provided
    const groupedData = groupBy ? data.reduce((acc, row) => {
        const key = row[groupBy] || 'Other';
        if (!acc[key]) acc[key] = [];
        acc[key].push(row);
        return acc;
    }, {}) : {};

    // Sort rows within each group based on sortConfig
    if (groupBy) {
        Object.keys(groupedData).forEach(key => {
            groupedData[key].sort((a, b) => {
                // Impact Priority Helper
                const impactValue = (impact) => {
                    const normalizedImpact = (impact || '').toUpperCase();
                    switch (normalizedImpact) {
                        case 'VERY HIGH': return 4;
                        case 'HIGH': return 3;
                        case 'MEDIUM': return 2;
                        case 'LOW': return 1;
                        default: return 0;
                    }
                };

                // Primary Sort: Impact (if active)
                if (impactSortDirection) {
                    const impactA = impactValue(a.Impact);
                    const impactB = impactValue(b.Impact);
                    const comparison = impactSortDirection === 'asc' ? impactA - impactB : impactB - impactA;

                    if (comparison !== 0) return comparison;
                }

                // Secondary (or Primary) Sort: Date
                const dateA = new Date(a.Date || 0);
                const dateB = new Date(b.Date || 0);
                return dateSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
            });
        });
    }

    // Ensure all groups in groupOrder are present
    if (groupBy && groupOrder) {
        groupOrder.forEach(group => {
            if (!groupedData[group]) {
                groupedData[group] = [];
            }
        });
    }

    // Sort groups if groupOrder is provided
    const sortedGroupEntries = groupBy ? Object.entries(groupedData).sort(([keyA], [keyB]) => {
        if (!groupOrder) return 0;
        const indexA = groupOrder.indexOf(keyA);
        const indexB = groupOrder.indexOf(keyB);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
    }) : [];

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: prev[groupKey] === false ? true : false
        }));
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex && result.source.droppableId === result.destination.droppableId) return;

        let movedRow, targetRow;

        if (groupBy) {
            // Handle grouped drag and drop
            const sourceGroupKey = result.source.droppableId.replace('group-', '');
            const destGroupKey = result.destination.droppableId.replace('group-', '');

            if (sourceGroupKey !== destGroupKey) return; // Only allow reordering within the same group for now

            const groupRows = groupedData[sourceGroupKey];
            movedRow = groupRows[sourceIndex];

            // We need to find the global index of the target position in the group
            // This is tricky because we need to know what row is currently at the destination index in that group
            // But wait, reorderRows takes global indices.

            // If we are moving within the same group:
            // We get the row that is currently at the destination index in that group
            const targetRowInGroup = groupRows[destinationIndex];

            const globalSourceIndex = getOriginalIndex(movedRow);
            const globalDestinationIndex = getOriginalIndex(targetRowInGroup);

            reorderRows(globalSourceIndex, globalDestinationIndex);

        } else {
            // Standard flat list drag and drop
            movedRow = data[sourceIndex];
            targetRow = data[destinationIndex];

            const globalSourceIndex = getOriginalIndex(movedRow);
            const globalDestinationIndex = getOriginalIndex(targetRow);

            reorderRows(globalSourceIndex, globalDestinationIndex);
        }
    };
    const [originalDataSnapshot, setOriginalDataSnapshot] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());

    // Context Menu State
    const [rowsToDelete, setRowsToDelete] = useState(null);

    const tableWrapperRef = useRef(null);
    const scrollRailXRef = useRef(null);
    const scrollRailYRef = useRef(null);

    // Horizontal Scroll State
    const [thumbWidth, setThumbWidth] = useState("38%");
    const [thumbLeft, setThumbLeft] = useState("0%");
    const [showScrollX, setShowScrollX] = useState(false);
    const isDraggingX = useRef(false);
    const startX = useRef(0);
    const startScrollLeft = useRef(0);

    // Vertical Scroll State
    const [thumbHeight, setThumbHeight] = useState("38%");
    const [thumbTop, setThumbTop] = useState("0%");
    const [showScrollY, setShowScrollY] = useState(false);
    const isDraggingY = useRef(false);
    const startY = useRef(0);
    const startScrollTop = useRef(0);

    // Scroll Logic
    const handleScroll = () => {
        if (tableWrapperRef.current) {
            if (isDraggingX.current || isDraggingY.current) return;

            const {
                scrollLeft, scrollWidth, clientWidth,
                scrollTop, scrollHeight, clientHeight
            } = tableWrapperRef.current;

            const maxScrollLeft = scrollWidth - clientWidth;
            if (maxScrollLeft > 0) {
                const scrollPercentageX = (scrollLeft / maxScrollLeft) * 100;
                const maxLeft = 100 - parseFloat(thumbWidth);
                const left = (scrollPercentageX / 100) * maxLeft;
                setThumbLeft(`${left}%`);
            }

            const maxScrollTop = scrollHeight - clientHeight;
            if (maxScrollTop > 0) {
                const scrollPercentageY = (scrollTop / maxScrollTop) * 100;
                const maxTop = 100 - parseFloat(thumbHeight);
                const top = (scrollPercentageY / 100) * maxTop;
                setThumbTop(`${top}%`);
            }
        }
    };

    useEffect(() => {
        const checkScroll = () => {
            if (tableWrapperRef.current) {
                const { scrollWidth, clientWidth, scrollHeight, clientHeight } = tableWrapperRef.current;
                const hasScrollX = scrollWidth > clientWidth;
                setShowScrollX(hasScrollX);
                if (hasScrollX) {
                    const widthPerc = (clientWidth / scrollWidth) * 100;
                    const finalWidth = Math.max(widthPerc, 10);
                    setThumbWidth(`${finalWidth}%`);
                }
                const hasScrollY = scrollHeight > clientHeight;
                setShowScrollY(hasScrollY);
                if (hasScrollY) {
                    const heightPerc = (clientHeight / scrollHeight) * 100;
                    const finalHeight = Math.max(heightPerc, 10);
                    setThumbHeight(`${finalHeight}%`);
                }
            }
        };
        checkScroll();
        window.addEventListener("resize", checkScroll);
        return () => window.removeEventListener("resize", checkScroll);
    }, [data]);

    // Drag Handlers
    const handleMouseDownX = (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDraggingX.current = true;
        startX.current = e.clientX;
        startScrollLeft.current = tableWrapperRef.current.scrollLeft;
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDownY = (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDraggingY.current = true;
        startY.current = e.clientY;
        startScrollTop.current = tableWrapperRef.current.scrollTop;
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (isDraggingX.current && tableWrapperRef.current && scrollRailXRef.current) {
            e.preventDefault();
            const { scrollWidth, clientWidth } = tableWrapperRef.current;
            const railWidth = scrollRailXRef.current.clientWidth;
            const thumbWidthPx = (parseFloat(thumbWidth) / 100) * railWidth;
            const maxScrollLeft = scrollWidth - clientWidth;
            const availableRail = railWidth - thumbWidthPx;
            const deltaX = e.clientX - startX.current;
            const scrollRatio = maxScrollLeft / availableRail;
            tableWrapperRef.current.scrollLeft = startScrollLeft.current + (deltaX * scrollRatio);
            const scrollPercentageX = (tableWrapperRef.current.scrollLeft / maxScrollLeft) * 100;
            const maxLeft = 100 - parseFloat(thumbWidth);
            const left = (scrollPercentageX / 100) * maxLeft;
            setThumbLeft(`${left}%`);
        }
        if (isDraggingY.current && tableWrapperRef.current && scrollRailYRef.current) {
            e.preventDefault();
            const { scrollHeight, clientHeight } = tableWrapperRef.current;
            const railHeight = scrollRailYRef.current.clientHeight;
            const thumbHeightPx = (parseFloat(thumbHeight) / 100) * railHeight;
            const maxScrollTop = scrollHeight - clientHeight;
            const availableRail = railHeight - thumbHeightPx;
            const deltaY = e.clientY - startY.current;
            const scrollRatio = maxScrollTop / availableRail;
            tableWrapperRef.current.scrollTop = startScrollTop.current + (deltaY * scrollRatio);
            const scrollPercentageY = (tableWrapperRef.current.scrollTop / maxScrollTop) * 100;
            const maxTop = 100 - parseFloat(thumbHeight);
            const top = (scrollPercentageY / 100) * maxTop;
            setThumbTop(`${top}%`);
        }
    };

    const handleMouseUp = () => {
        isDraggingX.current = false;
        isDraggingY.current = false;
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const confirmDelete = () => {
        if (rowsToDelete && rowsToDelete.length > 0) {
            // We need to delete rows. 
            // Important: Delete from highest index to lowest to avoid index shifting issues if we were deleting by index directly.
            // But deleteRow takes an index.
            // Let's get all original indices first.
            const indicesToDelete = rowsToDelete.map(row => getOriginalIndex(row)).filter(idx => idx !== -1).sort((a, b) => b - a);

            indicesToDelete.forEach(index => {
                deleteRow(index);
            });

            trackEvent('DELETE_ROWS', `Deleted ${indicesToDelete.length} rows in ${title}`);
            setRowsToDelete(null);
            setSelectedRows(new Set());
        }
    };

    const handleCellUpdate = (row, field, newValue) => {
        const index = getOriginalIndex(row);
        if (index !== -1) {
            updateRow(index, { ...row, [field]: newValue });
            trackEvent('UPDATE_ROW', `Updated ${field} in ${title}`);
        }
    };

    const handleEditModeToggle = () => {
        if (!isEditMode) {
            // Entering Edit Mode: Snapshot current data
            setOriginalDataSnapshot(JSON.parse(JSON.stringify(rows)));
            setIsEditMode(true);
            setSelectedRows(new Set());
        } else {
            // Saving (default behavior of button if it was just a toggle)
            // But we will separate Save and Cancel
            setIsEditMode(false);
            setOriginalDataSnapshot(null);
            setSelectedRows(new Set());
        }
    };

    const handleSave = () => {
        setIsEditMode(false);
        setOriginalDataSnapshot(null);
        setSelectedRows(new Set());
    };

    const handleCancel = () => {
        if (originalDataSnapshot) {
            setAllData(originalDataSnapshot);
        }
        setIsEditMode(false);
        setOriginalDataSnapshot(null);
        setSelectedRows(new Set());
    };

    const handleRowClick = (row) => {
        if (isEditMode) {
            const newSelected = new Set(selectedRows);
            if (newSelected.has(row)) {
                newSelected.delete(row);
            } else {
                newSelected.add(row);
            }
            setSelectedRows(newSelected);
        }
    };

    const handleDeleteSelected = () => {
        if (selectedRows.size > 0) {
            setRowsToDelete(Array.from(selectedRows));
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(new Set(data));
        } else {
            setSelectedRows(new Set());
        }
    };

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle>{title}</PanelTitle>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '180px' }}>
                        <CustomSelect
                            value={impactFilter}
                            onChange={setImpactFilter}
                            options={[
                                "All",
                                "Very High",
                                "High",
                                "Medium",
                                "Low"
                            ]}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <BtnIcon onClick={() => {
                            copyTableAsJSON(data);
                            trackEvent('COPY_TABLE', `Copied table ${title}`);
                        }} title="Copy Table JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                        {isEditMode ? (
                            <>
                                <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                                <Btn primary onClick={() => {
                                    addRow(defaultRowData);
                                    trackEvent('ADD_ROW', `Added row to ${title}`);
                                }} style={{ width: '30px', padding: 0 }}>+</Btn>
                                <Btn
                                    danger
                                    onClick={handleDeleteSelected}
                                    disabled={selectedRows.size === 0}
                                    style={{
                                        minWidth: '30px',
                                        padding: '0 8px',
                                        opacity: selectedRows.size === 0 ? 0.5 : 1,
                                        cursor: selectedRows.size === 0 ? 'not-allowed' : 'pointer',
                                        background: selectedRows.size > 0 ? '#d32f2f' : undefined,
                                        borderColor: selectedRows.size > 0 ? '#b71c1c' : undefined,
                                        color: selectedRows.size > 0 ? '#fff' : undefined
                                    }}
                                >
                                    - {selectedRows.size > 0 && `(${selectedRows.size})`}
                                </Btn>
                                <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                                <Btn primary onClick={handleSave}>
                                    Save
                                </Btn>
                                <Btn danger onClick={handleCancel}>
                                    Cancel
                                </Btn>
                            </>
                        ) : (
                            <BtnIcon onClick={handleEditModeToggle} title="Edit Mode">
                                ✎
                            </BtnIcon>
                        )}
                    </div>
                </div>
            </PanelHeader>

            <TableWrapper ref={tableWrapperRef} onScroll={handleScroll}>
                <Table>
                    <Thead>
                        <tr>
                            {isEditMode && (
                                <Th style={{ width: '40px', textAlign: 'center' }}>
                                    <CustomCheckbox
                                        checked={data.length > 0 && selectedRows.size === data.length}
                                        indeterminate={selectedRows.size > 0 && selectedRows.size < data.length}
                                        onChange={handleSelectAll}
                                    />
                                </Th>
                            )}
                            {isEditMode && <Th style={{ width: '50px' }}>ID</Th>}
                            <Th
                                style={{ width: '100px', minWidth: '100px', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setDateSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    Date
                                    {dateSortDirection === 'desc'
                                        ? <ArrowDownward style={{ fontSize: '14px', opacity: 0.7 }} />
                                        : <ArrowUpward style={{ fontSize: '14px', opacity: 0.7 }} />
                                    }
                                </div>
                            </Th>
                            <Th style={{ width: '170px', minWidth: '170px' }}>Catalyst</Th>
                            <Th style={{ minWidth: '550px' }}>Headline</Th>
                            <Th
                                style={{ width: '85px', minWidth: '85px', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setImpactSortDirection(prev => {
                                    if (prev === null) return 'desc';
                                    if (prev === 'desc') return 'asc';
                                    return null;
                                })}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                    Impact
                                    {impactSortDirection === 'desc' && <ArrowDownward style={{ fontSize: '14px', opacity: 0.7 }} />}
                                    {impactSortDirection === 'asc' && <ArrowUpward style={{ fontSize: '14px', opacity: 0.7 }} />}
                                </div>
                            </Th>
                            <Th style={{ minWidth: '460px' }}>Interpretation</Th>
                            <Th style={{ width: '140px', minWidth: '140px' }}>Key Assets</Th>
                            <Th style={{ width: '130px', minWidth: '130px' }}>Source</Th>
                            {showNextRelease && <Th style={{ width: '120px', minWidth: '120px' }}>Next Release</Th>}
                        </tr>
                    </Thead>
                    <DragDropContext onDragEnd={onDragEnd}>
                        {groupBy ? (
                            sortedGroupEntries.map(([groupKey, groupRows]) => {
                                const isExpanded = expandedGroups[groupKey] !== false; // Default to expanded
                                return (
                                    <React.Fragment key={groupKey}>
                                        <tbody>
                                            <SectionHeaderRow
                                                onClick={() => toggleGroup(groupKey)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <SectionHeaderCell colSpan={isEditMode ? 10 : 8}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span>
                                                                {groupKey} <span style={{ opacity: 0.5, fontSize: '0.85em', marginLeft: '4px' }}>({groupRows.length})</span>
                                                            </span>
                                                            {groupDescriptions && groupDescriptions[groupKey] && (
                                                                <InfoIconWithTooltip title={groupDescriptions[groupKey]} />
                                                            )}
                                                        </div>
                                                        <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                                            {isExpanded ? '▼' : '▶'}
                                                        </span>
                                                    </div>
                                                </SectionHeaderCell>
                                            </SectionHeaderRow>
                                        </tbody>
                                        {isExpanded && (
                                            <Droppable droppableId={`group-${groupKey}`}>
                                                {(provided) => (
                                                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                                        {groupRows.length === 0 ? (
                                                            <Tr>
                                                                <Td colSpan={isEditMode ? 10 : 8} style={{ textAlign: 'center', padding: '12px', opacity: 0.5, fontStyle: 'italic' }}>
                                                                    No rows in this group
                                                                </Td>
                                                            </Tr>
                                                        ) : (
                                                            groupRows.map((row, index) => (
                                                                <Draggable key={row.id || index} draggableId={`row-${getOriginalIndex(row)}`} index={index} isDragDisabled={!isEditMode}>
                                                                    {(provided, snapshot) => (
                                                                        <Tr
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            onClick={() => handleRowClick(row)}
                                                                            style={{
                                                                                ...provided.draggableProps.style,
                                                                                background: isEditMode && selectedRows.has(row) ? 'rgba(128, 203, 196, 0.1)' : snapshot.isDragging ? 'rgba(128, 203, 196, 0.15)' : 'transparent',
                                                                                cursor: isEditMode ? 'pointer' : 'default',
                                                                                display: 'table-row'
                                                                            }}
                                                                        >
                                                                            {isEditMode && (
                                                                                <Td style={{ width: '40px', textAlign: 'center' }}>
                                                                                    <CustomCheckbox
                                                                                        checked={selectedRows.has(row)}
                                                                                        onChange={() => handleRowClick(row)}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    />
                                                                                </Td>
                                                                            )}
                                                                            {isEditMode && (
                                                                                <Td style={{ width: '50px' }}>
                                                                                    <TdSmall style={{ opacity: 0.5 }}>{row.id}</TdSmall>
                                                                                </Td>
                                                                            )}
                                                                            <Td style={{ width: '100px', minWidth: '100px' }}>
                                                                                {isEditMode ? (
                                                                                    <EditableCell value={row.Date} onChange={(val) => handleCellUpdate(row, 'Date', val)} />
                                                                                ) : (
                                                                                    <TdSmall>{row.Date}</TdSmall>
                                                                                )}
                                                                            </Td>
                                                                            <Td style={{ width: '170px', minWidth: '170px' }}>
                                                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                                    {isEditMode ? (
                                                                                        <>
                                                                                            <EditableCell value={row.Catalyst} onChange={(val) => handleCellUpdate(row, 'Catalyst', val)} />
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <CatalystText style={{ fontSize: '12px' }}>{row.Catalyst}</CatalystText>
                                                                                            {row.Tag && <span style={{ fontSize: '10px', opacity: 0.8 }}>{row.Tag}</span>}
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </Td>

                                                                            <Td style={{ minWidth: '550px', whiteSpace: 'normal' }}>
                                                                                {isEditMode ? (
                                                                                    <EditableCell type="textarea" value={row.Headline} onChange={(val) => handleCellUpdate(row, 'Headline', val)} />
                                                                                ) : (
                                                                                    row.Headline
                                                                                )}
                                                                            </Td>
                                                                            <Td style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>
                                                                                {isEditMode ? (
                                                                                    <EditableCell value={row.Impact} onChange={(val) => handleCellUpdate(row, 'Impact', val)} />
                                                                                ) : (
                                                                                    <ImpactBadge level={row.Impact}>{row.Impact}</ImpactBadge>
                                                                                )}
                                                                            </Td>
                                                                            <Td style={{ minWidth: '460px', whiteSpace: 'normal' }}>
                                                                                {isEditMode ? (
                                                                                    <EditableCell type="textarea" value={row.Interpretation} onChange={(val) => handleCellUpdate(row, 'Interpretation', val)} />
                                                                                ) : (
                                                                                    <TdSmall>{row.Interpretation}</TdSmall>
                                                                                )}
                                                                            </Td>
                                                                            <Td style={{ width: '140px', minWidth: '140px' }}>
                                                                                {isEditMode ? (
                                                                                    <EditableCell value={row.Assets} onChange={(val) => handleCellUpdate(row, 'Assets', val)} />
                                                                                ) : (
                                                                                    <TdSmall>{row.Assets}</TdSmall>
                                                                                )}
                                                                            </Td>
                                                                            <Td style={{ width: '130px', minWidth: '130px' }}>
                                                                                {isEditMode ? (
                                                                                    <EditableCell value={row.Sources} onChange={(val) => handleCellUpdate(row, 'Sources', val)} />
                                                                                ) : (
                                                                                    <TdSmall>{row.Sources}</TdSmall>
                                                                                )}
                                                                            </Td>

                                                                            {showNextRelease && (
                                                                                <Td style={{ width: '120px', minWidth: '120px' }}>
                                                                                    {isEditMode ? (
                                                                                        <EditableCell value={row.next_release} onChange={(val) => handleCellUpdate(row, 'next_release', val)} />
                                                                                    ) : (
                                                                                        <TdSmall>{formatNextRelease(row.next_release)}</TdSmall>
                                                                                    )}
                                                                                </Td>
                                                                            )}
                                                                        </Tr>
                                                                    )}
                                                                </Draggable>
                                                            ))
                                                        )}
                                                        {provided.placeholder}
                                                    </tbody>
                                                )}
                                            </Droppable>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <Droppable droppableId="economic-rows">
                                {(provided) => (
                                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                        {data.length > 0 ? (
                                            data.map((row, index) => (
                                                <Draggable key={row.id || index} draggableId={`row-${getOriginalIndex(row)}`} index={index} isDragDisabled={!isEditMode}>
                                                    {(provided, snapshot) => (
                                                        <Tr
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            onClick={() => handleRowClick(row)}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                background: isEditMode && selectedRows.has(row) ? 'rgba(128, 203, 196, 0.1)' : snapshot.isDragging ? 'rgba(128, 203, 196, 0.15)' : 'transparent',
                                                                cursor: isEditMode ? 'pointer' : 'default',
                                                                display: 'table-row'
                                                            }}
                                                        >
                                                            {isEditMode && (
                                                                <Td style={{ width: '40px', textAlign: 'center' }}>
                                                                    <CustomCheckbox
                                                                        checked={selectedRows.has(row)}
                                                                        onChange={() => handleRowClick(row)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </Td>
                                                            )}
                                                            {isEditMode && (
                                                                <Td style={{ width: '50px' }}>
                                                                    <TdSmall style={{ opacity: 0.5 }}>{row.id}</TdSmall>
                                                                </Td>
                                                            )}
                                                            <Td style={{ width: '100px', minWidth: '100px' }}>
                                                                {isEditMode ? (
                                                                    <EditableCell value={row.Date} onChange={(val) => handleCellUpdate(row, 'Date', val)} />
                                                                ) : (
                                                                    <TdSmall>{row.Date}</TdSmall>
                                                                )}
                                                            </Td>
                                                            <Td style={{ width: '170px', minWidth: '170px' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                    {isEditMode ? (
                                                                        <>
                                                                            <EditableCell value={row.Catalyst} onChange={(val) => handleCellUpdate(row, 'Catalyst', val)} />
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <CatalystText style={{ fontSize: '12px' }}>{row.Catalyst}</CatalystText>
                                                                            {row.Tag && <span style={{ fontSize: '10px', opacity: 0.8 }}>{row.Tag}</span>}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </Td>
                                                            <Td style={{ minWidth: '550px', whiteSpace: 'normal' }}>
                                                                {isEditMode ? (
                                                                    <EditableCell type="textarea" value={row.Headline} onChange={(val) => handleCellUpdate(row, 'Headline', val)} />
                                                                ) : (
                                                                    row.Headline
                                                                )}
                                                            </Td>
                                                            <Td style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>
                                                                {isEditMode ? (
                                                                    <EditableCell value={row.Impact} onChange={(val) => handleCellUpdate(row, 'Impact', val)} />
                                                                ) : (
                                                                    <ImpactBadge level={row.Impact}>{row.Impact}</ImpactBadge>
                                                                )}
                                                            </Td>
                                                            <Td style={{ minWidth: '460px', whiteSpace: 'normal' }}>
                                                                {isEditMode ? (
                                                                    <EditableCell type="textarea" value={row.Interpretation} onChange={(val) => handleCellUpdate(row, 'Interpretation', val)} />
                                                                ) : (
                                                                    <TdSmall>{row.Interpretation}</TdSmall>
                                                                )}
                                                            </Td>

                                                            <Td style={{ width: '140px', minWidth: '140px' }}>
                                                                {isEditMode ? (
                                                                    <EditableCell value={row.Assets} onChange={(val) => handleCellUpdate(row, 'Assets', val)} />
                                                                ) : (
                                                                    <TdSmall>{row.Assets}</TdSmall>
                                                                )}
                                                            </Td>
                                                            <Td style={{ width: '130px', minWidth: '130px' }}>
                                                                {isEditMode ? (
                                                                    <EditableCell value={row.Sources} onChange={(val) => handleCellUpdate(row, 'Sources', val)} />
                                                                ) : (
                                                                    <TdSmall>{row.Sources}</TdSmall>
                                                                )}
                                                            </Td>

                                                            {showNextRelease && (
                                                                <Td style={{ width: '120px', minWidth: '120px' }}>
                                                                    {isEditMode ? (
                                                                        <EditableCell value={row.next_release} onChange={(val) => handleCellUpdate(row, 'next_release', val)} />
                                                                    ) : (
                                                                        <TdSmall>{formatNextRelease(row.next_release)}</TdSmall>
                                                                    )}
                                                                </Td>
                                                            )}
                                                        </Tr>
                                                    )}
                                                </Draggable>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan={isEditMode ? 10 : 8} style={{ textAlign: "center", padding: "20px", color: "#607d8b" }}>
                                                    No data available for this category.
                                                </Td>
                                            </Tr>
                                        )}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        )}
                    </DragDropContext>
                </Table>
            </TableWrapper>

            {showScrollX && (
                <ScrollRail ref={scrollRailXRef}>
                    <ScrollThumb
                        width={thumbWidth}
                        left={thumbLeft}
                        onMouseDown={handleMouseDownX}
                    />
                </ScrollRail>
            )}

            {showScrollY && (
                <ScrollRailVertical ref={scrollRailYRef}>
                    <ScrollThumbVertical
                        height={thumbHeight}
                        top={thumbTop}
                        onMouseDown={handleMouseDownY}
                    />
                </ScrollRailVertical>
            )}

            {rowsToDelete && (
                <ModalOverlay>
                    <ModalContent>
                        <h3 style={{ color: '#fff', marginTop: 0 }}>Confirm Deletion</h3>
                        <p style={{ color: '#b0bec5' }}>
                            Are you sure you want to delete {rowsToDelete.length} row{rowsToDelete.length > 1 ? 's' : ''}?
                            This action cannot be undone.
                        </p>
                        <ButtonRow>
                            <Btn onClick={() => setRowsToDelete(null)}>Cancel</Btn>
                            <Btn primary onClick={confirmDelete} style={{ background: '#ef5350', borderColor: '#ef5350' }}>Delete</Btn>
                        </ButtonRow>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Panel>
    );
};

export default EconomicTable;
